import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import { cn } from '../utils/cn';
function MenuIcon() {
    return (_jsx("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: _jsx("path", { d: "M4 6h16M4 12h16M4 18h16" }) }));
}
/* Off-Canvas unter 1024 px (vgl. components.css). Nur dort ist die Sidebar
   ein Overlay, das Escape, inert und Fokus-Rueckgabe braucht. */
const OFF_CANVAS_QUERY = '(max-width: 1024px)';
function isOffCanvas() {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia(OFF_CANVAS_QUERY).matches
        : false;
}
/**
 * Einheitlicher App-Rahmen (Saeule A): Top-Bar (48 px) mit Marke, App-Switcher,
 * Suche, Aktionen (Notification-Center) und Profil — plus optionale Sidebar
 * (240 px, `--sec-sidebar-bg`) und scrollbarer Hauptbereich. Apps liefern nur
 * Inhalt; die Shell besitzt die Chrome. Optik kommt vollstaendig aus den
 * `.sec-app-shell*`-Klassen des Design-Systems (kein Inline-Styling).
 *
 * Barrierefreiheit: Skip-Link zum Hauptinhalt; die mobile Off-Canvas-Sidebar
 * schliesst mit Escape, sperrt den Rest der Seite per `inert`, fokussiert
 * ihren ersten Eintrag und gibt den Fokus an den Menue-Button zurueck.
 */
export function AppShell({ brand, appSwitcher, search, actions, profile, sidebar, sidebarOpen = false, onSidebarToggle, menuLabel = 'Navigation', sidebarLabel = 'Hauptnavigation', skipLinkLabel = 'Zum Inhalt springen', mainId = 'sec-main', barStyle, children, className, }) {
    const hasSidebar = Boolean(sidebar);
    const navRef = useRef(null);
    const headerRef = useRef(null);
    const mainRef = useRef(null);
    const menuBtnRef = useRef(null);
    const wasOpen = useRef(false);
    useEffect(() => {
        if (!hasSidebar || !onSidebarToggle)
            return;
        const offCanvas = isOffCanvas();
        if (sidebarOpen && offCanvas) {
            const onKey = (e) => {
                if (e.key === 'Escape')
                    onSidebarToggle(false);
            };
            document.addEventListener('keydown', onKey);
            // Rest der Seite fuer Tastatur und Screenreader sperren, Fokus in die Navigation.
            // Die Knoten im Effekt festhalten: beim Cleanup koennen die Refs schon
            // auf andere Knoten zeigen (react-hooks/exhaustive-deps).
            const header = headerRef.current;
            const main = mainRef.current;
            header?.setAttribute('inert', '');
            main?.setAttribute('inert', '');
            const first = navRef.current?.querySelector('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
            (first ?? navRef.current)?.focus?.({ preventScroll: true });
            wasOpen.current = true;
            return () => {
                document.removeEventListener('keydown', onKey);
                header?.removeAttribute('inert');
                main?.removeAttribute('inert');
            };
        }
        if (!sidebarOpen && wasOpen.current) {
            wasOpen.current = false;
            menuBtnRef.current?.focus({ preventScroll: true });
        }
        return undefined;
    }, [sidebarOpen, hasSidebar, onSidebarToggle]);
    return (_jsxs("div", { className: cn('sec-app-shell', className), children: [skipLinkLabel ? (_jsx("a", { className: "sec-skip-link", href: `#${mainId}`, children: skipLinkLabel })) : null, _jsxs("header", { ref: headerRef, className: cn('sec-app-shell__bar', barStyle && 'sec-app-shell__bar--branded'), style: barStyle, children: [hasSidebar && onSidebarToggle ? (_jsx("button", { ref: menuBtnRef, type: "button", className: "sec-icon-btn sec-app-shell__menu-btn", "aria-label": menuLabel, "aria-expanded": sidebarOpen, "aria-controls": hasSidebar ? `${mainId}-sidebar` : undefined, onClick: () => onSidebarToggle(!sidebarOpen), children: _jsx(MenuIcon, {}) })) : null, appSwitcher, brand ? _jsx("div", { className: "sec-app-shell__brand", children: brand }) : null, search ? _jsx("div", { className: "sec-app-shell__search", children: search }) : _jsx("div", { className: "sec-app-shell__spacer" }), _jsxs("div", { className: "sec-app-shell__actions", children: [actions, profile] })] }), _jsxs("div", { className: "sec-app-shell__body", children: [hasSidebar ? (_jsxs(_Fragment, { children: [_jsx("nav", { ref: navRef, id: `${mainId}-sidebar`, className: cn('sec-app-shell__sidebar', sidebarOpen && 'sec-app-shell__sidebar--open'), "aria-label": sidebarLabel, tabIndex: -1, children: sidebar }), sidebarOpen && onSidebarToggle ? (_jsx("div", { className: "sec-app-shell__backdrop", "aria-hidden": "true", onClick: () => onSidebarToggle(false) })) : null] })) : null, _jsx("main", { ref: mainRef, id: mainId, className: "sec-app-shell__main", tabIndex: -1, children: children })] })] }));
}
